import React, {ReactNode} from 'react'
import styled from 'styled-components'
import {Box} from '@mui/material'
import {ApplyTitle, FirstPageDiv, HeaderArea, HeaderAreaBox} from './NatAdminApplicatioPrintCommonStyle'
import {getPrintDate} from '../../helper/NatApplicationHelper'
import {StyledDiv} from '../../../../../../styledComponents/StyledTypeDefinitions'
import {NatParkingUsesDetail} from '../../../repository/NatGetParkingUses'
import {NatSealField, SealField} from './NatSealField'
import {NatEncloseInParenthesis} from './NatEncloseInParenthesis'
import moment from 'moment'
import {RESIDENT_APPLY_PROCESS, RESIDENT_APPLY_STATUS, USER_ATTRIBUTE} from '../../../../../../CodeMaster'
import {ApplyStatusInfo} from '../../../repository/NatGetApplicationStatus'
import {convertToRoomNumberForResident} from '../../../../../common/util/Natutil'

interface Props {
    parkingUseApplicationDocumentParam: NatParkingUsesDetail
    applyStatusInfoList: ApplyStatusInfo[]
}

export const NatParkingUseApplicationDocument: React.FC<Props> = props => {
    const {
        parkingUseApplicationDocumentParam,
        applyStatusInfoList
    } = props

    const {
        applyId, condominiumName, ridgeName, roomNumber,
        applicantFamilyName, applicantFirstName, useArea, useStartDate,
        userFamilyName, userFirstName, userAttribute, ownerFamilyName,
        ownerFirstName, carBodyShape, carName, carRegistrationNumber,
        engineSize, length, height, width,
        vehicleWeight, vehicleTotalWeight
    } = parkingUseApplicationDocumentParam

    const applicantRoom = `${ridgeName} ${convertToRoomNumberForResident(roomNumber)}`
    const applicantName = `${applicantFamilyName} ${applicantFirstName}`
    const userName = `${userFamilyName} ${userFirstName} （${USER_ATTRIBUTE.codeToEnumConstants(userAttribute).label}）`
    const ownerName = `${ownerFamilyName} ${ownerFirstName}`

    return (<>
        <FirstPageDiv>
            <HeaderArea>
                <HeaderAreaBox>
                    <Box ml={'auto'} mt={'10mm'}>申請ID：{applyId}</Box>
                </HeaderAreaBox>
                <HeaderAreaBox>
                    <Box m={'auto'}><ApplyTitle>駐車場使用申込書</ApplyTitle></Box>
                </HeaderAreaBox>
                <HeaderAreaBox>
                    <Box ml={'auto'} mt={'3mm'}>
                        {renderSealField(applyStatusInfoList)}
                    </Box>
                </HeaderAreaBox>
            </HeaderArea>
            <Box ml={'25mm'} mt={'3mm'}>
                理事長殿
            </Box>
            <Box ml={'30mm'} mt={'5mm'} whiteSpace={'pre-wrap'}>
                　私は、本マンションの駐車場を使用したいので、駐車場使用規則に掲げる収容可能車<br/>
                制限・区画寸法等を確認のうえ、次の通り申込をいたします。
            </Box>
            <Box margin={'3mm 0 0 30mm'} display={'flex'} flexDirection={'column'} width={'130mm'}>
                <Box>
                    <Box mb={'5mm'} whiteSpace={'pre-wrap'}>1  申請者</Box>
                    <Box ml={'8mm'} mb={'5mm'}>
                        {renderApplicationDetail('マンション名', condominiumName)}
                        {renderApplicationDetail('部屋番号', applicantRoom)}
                        {renderApplicationDetail('氏名', applicantName)}
                    </Box>
                </Box>
                <Box>
                    <Box mb={'5mm'}>
                        {renderApplicationDetail('2  使用区画', useArea)}
                    </Box>
                </Box>
                <Box>
                    <Box mb={'5mm'}>
                        {renderApplicationDetail('3  使用開始日', renderUseStartDate(useStartDate))}
                    </Box>
                </Box>
                <Box>
                    <Box mb={'5mm'}>
                        {renderApplicationDetail('4  使用者', userName)}
                    </Box>
                </Box>
                <Box mb={'5mm'}>
                    <Box mb={'5mm'} whiteSpace={'pre-wrap'}>5  車両情報</Box>
                    <Box ml={'8mm'}>
                        {renderApplicationDetail('所有者', ownerName)}
                        {renderApplicationDetail('車体の形状', carBodyShape)}
                        {renderApplicationDetail('車名', carName)}
                        {renderApplicationDetail('自動車登録番号\n' + ' または車両番号',
                            carRegistrationNumber)}
                        {renderApplicationDetail('排気量', engineSize + 'L')}
                        {renderApplicationDetail('外寸\n' + '（長さ/高さ/幅）',
                            length + 'cm/' +
                            height + 'cm/' +
                            width + 'cm')}
                        {renderApplicationDetail('車両重量', vehicleWeight + 'kg')}
                        {renderApplicationDetail('車両総重量', vehicleTotalWeight + 'kg')}
                    </Box>
                </Box>
            </Box>
        </FirstPageDiv>
    </>)

    NatParkingUseApplicationDocument.displayName = 'NatParkingUseApplicationDocument'
}

const renderApplicationDetail = (title: string, value: string | ReactNode) => {
    return (
        <PrintApplicationDetailContainer>
            <Box whiteSpace={'pre-wrap'}>{title}</Box>
            <NatEncloseInParenthesis item={value}/>
        </PrintApplicationDetailContainer>
    )
}

const renderUseStartDate = (date: string) => {
    const value = getPrintDate(date)

    return (
        <>
            <Box component={'span'} padding={'0 3mm 0 1mm'}>{value.year}</Box>年
            <Box component={'span'} padding={'0 4mm 0 4mm'}>{value.month}</Box>月
            <Box component={'span'} padding={'0 4mm 0 4mm'}>{value.day}</Box>日
        </>
    )
}

const renderSealField = (applyStatusInfoList: ApplyStatusInfo[]) => {
    const sealFields: SealField[] = applyStatusInfoList.map((info) => {
        const applyStatusEnum = RESIDENT_APPLY_PROCESS.codeToEnumConstants(info.applyProcess)
        let seal
        if (info.applyStatus === RESIDENT_APPLY_STATUS.REJECTION.value) {
            seal = '却下'
        } else {
            seal = info.actual.registerDatetime ? moment(info.actual.registerDatetime).format('YYYY/M/D') : ''
        }
        return {
            applyStatus: applyStatusEnum.label,
            seal: seal,
            sortOrder: applyStatusEnum.displayOrder
        }
    });

    return (
        <NatSealField sealFields={sealFields}/>
    )
}

const PrintApplicationDetailContainer: StyledDiv = styled.div`
  display: flex;
  width: 100%;
`
